import { useLayoutEffect } from 'react';

/**
 * useBodyScrollLock — robust, iOS-proof body scroll lock for modals/overlays.
 *
 * Why this exists:
 *   Setting `document.body.style.overflow = 'hidden'` is enough on Android/desktop
 *   but iOS Safari ignores it for touch panning — the page still rubber-bands.
 *   Because our `.modal-overlay` is `position: fixed`, that page bounce visibly
 *   drags the whole modal around when the user touches a non-scrollable region
 *   (header, profile photo, tabs). The fix is to actually pin the document by
 *   making <body> `position: fixed` and offsetting it by the current scroll
 *   position, then restoring scroll on unlock.
 *
 * Ref-counted so stacked/nested modals don't fight over body styles: only the
 * first lock pins the body and only the last unlock restores it.
 *
 * @param {boolean} isLocked - lock the body scroll while true.
 */

let lockCount = 0;
let savedScrollY = 0;
let savedStyles = null;

const lockBody = () => {
  lockCount += 1;
  // Already pinned by an outer modal — nothing more to do.
  if (lockCount > 1) return;

  const { body } = document;
  savedScrollY = window.scrollY || window.pageYOffset || 0;
  savedStyles = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
  };

  // Pin the document so iOS cannot rubber-band the page behind the overlay.
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${savedScrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
};

const unlockBody = () => {
  lockCount = Math.max(0, lockCount - 1);
  // Another modal is still open — keep the body pinned.
  if (lockCount > 0) return;

  const { body } = document;
  if (savedStyles) {
    body.style.overflow = savedStyles.overflow;
    body.style.position = savedStyles.position;
    body.style.top = savedStyles.top;
    body.style.left = savedStyles.left;
    body.style.right = savedStyles.right;
    body.style.width = savedStyles.width;
    savedStyles = null;
  }

  // Restore the scroll position the user was at before locking.
  window.scrollTo(0, savedScrollY);
};

export const useBodyScrollLock = (isLocked) => {
  useLayoutEffect(() => {
    if (!isLocked) return undefined;
    lockBody();
    return unlockBody;
  }, [isLocked]);
};

export default useBodyScrollLock;
